<i18n locale="en">
{
  "title": "Confirm delete",
  "message": "Are you sure you want to delete?",
  "cascade-label": "Also delete orphaned children",
  "actions": {
    "delete": "Delete"
  }
}
</i18n>

<template>
  <OwnDialog v-model="isVisible" @cancel="onCancel">
    <template #heading>
      <OwnDialogHeading :title="title ?? t('title')" @cancel="onCancel" />
    </template>
    <template #default>
      <OwnDialogContent scroll>
        <p
          class="text-paragraph-regular text-color-primary"
          v-text="message ?? t('message')"
        ></p>

        <label
          v-if="showCascadeDelete"
          class="align-center mt-4 flex-row gap-3"
        >
          <OwnCheckbox v-model="cascadeDeleteModel" />
          <OwnType
            :text="cascadeDeleteLabel ?? t('cascade-label')"
            variant="paragraph-regular"
            color="secondary"
            el="span"
          />
        </label>

        <slot name="extra" />
      </OwnDialogContent>
    </template>
    <template #footer="{ close }">
      <OwnDialogFooter>
        <OwnButton
          :text="deleteActionLabel ?? t('actions.delete')"
          :processing="isProcessing"
          :disabled="disabled"
          variant="danger"
          @click="onConfirmDelete(close)"
        />
      </OwnDialogFooter>
    </template>
  </OwnDialog>
</template>

<script>
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import { OwnCheckbox } from '@/ui/OwnCheckbox'
import {
  OwnDialog,
  OwnDialogHeading,
  OwnDialogFooter,
  OwnDialogContent,
} from '@/ui/OwnDialog'
import { OwnType } from '@/ui/OwnType'
import { mapModel } from '@/utils/computed'

export default {
  name: 'DeleteConfirmationDialog',
  components: {
    OwnButton,
    OwnCheckbox,
    OwnDialog,
    OwnDialogContent,
    OwnDialogFooter,
    OwnDialogHeading,
    OwnType,
  },
  emits: ['cancel', 'delete', 'update:modelValue', 'update:cascadeDelete'],
  setup() {
    const { t } = useI18n()
    return { t }
  },
  props: {
    /**
     * Current value of the cascade-delete checkbox. Bound via
     * `v-model:cascade-delete`. Only renders when `showCascadeDelete` is true.
     */
    cascadeDelete: { type: Boolean, default: false },

    /**
     * Override the cascade checkbox label
     */
    cascadeDeleteLabel: { type: String, default: undefined },

    /**
     * Override the delete action label
     */
    deleteActionLabel: { type: String, default: undefined },

    /**
     * Whether the confirm action is disabled or not
     */
    disabled: { type: Boolean, default: false },

    /**
     * Whether the confirm action is processing or not
     */
    isProcessing: { type: Boolean, default: false },

    /**
     * Override default message
     */
    message: { type: String, default: undefined },

    /**
     * Show Modal
     */
    modelValue: { type: Boolean, default: false },

    /**
     *
     * Whether to render the cascade-delete checkbox below the message.
     */
    showCascadeDelete: { type: Boolean, default: false },

    /**
     * Override default title
     */
    title: { type: String, default: undefined },
  },
  computed: {
    ...mapModel('isVisible'),
    cascadeDeleteModel: {
      get() {
        return this.cascadeDelete ?? false
      },
      set(value) {
        this.$emit('update:cascadeDelete', value)
      },
    },
  },
  methods: {
    onCancel() {
      this.$emit('cancel')
      this.isVisible = false
    },
    onConfirmDelete(close) {
      this.$emit('delete')
      if (close) {
        close()
      }
    },
  },
}
</script>
